import { Table } from 'antd'
import { forwardRef, useContext } from 'react'

import { Typography } from '@mui/material'
import { sumBy } from 'lodash'
import queryString from 'query-string'
import CircularLoading from '../../../../../components/CircularProgress'
import PageUrls from '../../../../../constants/page-urls'
import {
  IUserDailyReport,
  IUserDailyValueReport,
  IUserOrderCancelledReport,
} from '../../../../../types/report'
import { AbilityContext } from '../../../../../components/Ability'
import { numberFormat } from '../../../../../utils'
import { useUser } from '../../../../../hooks/useUser'
import { EOrderStatus } from '../../../../../types/order'
import { IFilterMapped } from '../../../components/CommissionReportFilter'
import { s } from '@fullcalendar/core/internal-common'
interface IDailySalesTableProps {
  data: IUserDailyReport[] | undefined
  isLoading?: boolean
  userCancelledReport?: IUserOrderCancelledReport[]
  filter: IFilterMapped
  searchFilter: IFilterMapped
  hideText?: boolean
}

const CommissionReportTable = forwardRef<
  HTMLTableElement,
  IDailySalesTableProps
>((props: IDailySalesTableProps, ref) => {
  const {
    data,
    isLoading,
    userCancelledReport,
    filter,
    searchFilter,
    hideText = false,
  } = props
  const ability = useContext(AbilityContext)
  const { merchant } = useUser()
  const showAll = ability.can('manage', 'Report', 'commission')

  //console.log(hideText, 'hideText')

  const onSelectUser = async (
    username: string,
    options?: Record<string, any>,
  ) => {
    const {
      categoryObjectIds: categories,
      productObjectIds: products,
      ...rest
    } = filter
    const query = queryString.stringify({
      ...rest,
      products,
      page: 1,
      perPage: 15,
      comission: username,
      categories: categories,
      ...options,
    })
    // console.log(query)
    window.open(`${PageUrls.Orders.Index}/?${query}`, '_blank')
  }

  const onCancelledSelectUser = async (username: string) => {
    const { categoryObjectIds: categories, ...rest } = filter
    const query = queryString.stringify({
      ...rest,
      page: 1,
      perPage: 15,
      username,
      categories: categories,
    })
    window.open(`${PageUrls.Report.CommissionCancelled}/?${query}`, '_blank')
  }

  const report = data ?? []

  const userComissionReduce = report?.reduce(
    (acc, cur) => {
      acc[cur.value.username] = cur
      return acc
    },
    {} as Record<string, IUserDailyReport>,
  )

  let userCancelledReduce = userCancelledReport?.reduce(
    (acc, cur) => {
      acc[cur.username] = cur
      if (userComissionReduce && !userComissionReduce[cur.username]) {
        report.push({
          value: {
            username: cur.username,
            count: 0,
            unit: '0',
            finalUnits: 0,
            discount: 0,
            subtotal: 0,
            total: 0,
            collection: 0,
            collectionUnits: 0,
            free: 0,
            codTotal: 0,
            bankTotal: 0,
            commission: 0,
            commissionPercent: 0,
            commissionOrder: 0,
            cancelled: false,
          },
          name: cur.username,
          date: '',
          merchant: '',
          type: '',
        })
      }
      return acc
    },
    {} as Record<string, IUserOrderCancelledReport>,
  )

  if (searchFilter.viewType === 'sale') {
    userCancelledReduce = {}
  }

  let columns = [
    {
      title: 'ขายโดย',
      dataIndex: 'name',
      align: 'center' as const,
      key: 'value',
      width: 80,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        return (
          <>
            {searchFilter.viewType === 'sale' ? (
              <Typography
                variant="subtitle2"
                textAlign={'left'}
                sx={{
                  color: 'success.main',
                  cursor: 'pointer',
                  lineHeight: 1.2,
                }}
                onClick={() =>
                  onSelectUser(data?.value?.username, {
                    exceptStatus: [
                      EOrderStatus.CANCLELLED,
                      EOrderStatus.RETURNED,
                    ],
                  })
                }
              >
                {data?.value?.username}
              </Typography>
            ) : (
              <Typography variant="subtitle2" textAlign={'left'}>
                {data?.value?.username}
              </Typography>
            )}
            <Typography
              variant="subtitle2"
              textAlign={'left'}
              color="text.secondary"
              fontSize={12}
            >
              {data?.name ?? '-'}
            </Typography>
          </>
        )
      },
    },
    {
      title: 'รายการ',
      dataIndex: 'orderAmount',
      align: 'center' as const,
      key: 'value',
      width: 30,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberFormat(
              Number(data.value?.count ?? 0) -
                Number(userCancelled?.count ?? 0),
            )}
          </Typography>
        )
      },
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'unitAmount',
      align: 'center' as const,
      width: 75,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberFormat(
                Number(data.value?.unit ?? 0) -
                  Number(userCancelled?.unit ?? 0),
              )}
            </Typography>
            {!hideText && (
              <Typography
                variant="subtitle2"
                fontSize={11}
                color="#aaa"
                textAlign={'center'}
              >
                (
                {numberFormat(
                  Number(data.value?.finalUnits ?? 0) -
                    Number(
                      (userCancelled?.collectionUnits ?? 0) +
                        (userCancelled?.unitNonCollection ?? 0),
                    ),
                )}
                )
              </Typography>
            )}
          </>
        )
      },
    },
    {
      title: 'เซต',
      dataIndex: 'set',
      align: 'center' as const,
      width: 60,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.collection ?? 0) -
              Number(userCancelled?.collection ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'เซต(ชิ้น)',
      dataIndex: 'setPieces',
      align: 'center' as const,
      width: 60,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.collectionUnits ?? 0) -
              Number(userCancelled?.collectionUnits ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'ฟรี(ชิ้น)',
      dataIndex: 'freePieces',
      align: 'center' as const,
      width: 60,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.free ?? 0) - Number(userCancelled?.free ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'COD',
      dataIndex: 'codPrice',
      align: 'center' as const,
      width: 70,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.codTotal ?? 0) -
              Number(userCancelled?.codTotal ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'ยอดโอน',
      dataIndex: 'bankPrice',
      align: 'center' as const,
      key: 'bankPrice',
      width: 70,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.bankTotal ?? 0) -
              Number(userCancelled?.bankTotal ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'รวม',
      dataIndex: 'total',
      align: 'center' as const,
      key: 'total',
      width: 70,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.bankTotal ?? 0) +
              Number(data.value?.codTotal ?? 0) -
              (Number(userCancelled?.bankTotal ?? 0) +
                Number(userCancelled?.codTotal ?? 0))
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'ค่าคอม (ชิ้น)',
      dataIndex: 'commission',
      align: 'center' as const,
      width: 70,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.commission ?? 0) -
              Number(userCancelled?.commission ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
    {
      title: 'ค่าคอม %',
      dataIndex: 'commissionPercent',
      align: 'center' as const,
      width: 70,
      render: (value: IUserDailyValueReport, data: IUserDailyReport) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {(
                Number(data.value?.commissionPercent ?? 0) -
                Number(userCancelled?.commissionPercent ?? 0)
              ).toLocaleString()}
            </Typography>
            {!hideText && (
              <Typography
                variant="subtitle2"
                fontSize={11}
                color="#aaa"
                textAlign={'center'}
              >
                (
                {numberFormat(
                  ((Number(data.value?.bankTotal ?? 0) +
                    Number(data.value?.codTotal ?? 0) -
                    (Number(userCancelled?.bankTotal ?? 0) +
                      Number(userCancelled?.codTotal ?? 0))) *
                    (merchant.config?.commissionPercent ?? 0)) /
                    100,
                )}
                )
              </Typography>
            )}
          </>
        )
      },
    },
    {
      title: 'ค่าคอม (ORDER)',
      dataIndex: 'commissionOrder',
      align: 'center' as const,
      width: 70,
      render: (
        commissionOrder: IUserDailyValueReport,
        data: IUserDailyReport,
      ) => {
        const userCancelled = userCancelledReduce?.[data?.value?.username]
        return (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              Number(data.value?.commissionOrder ?? 0) -
              Number(userCancelled?.commissionOrder ?? 0)
            ).toLocaleString()}
          </Typography>
        )
      },
    },
  ]

  if (!showAll) {
    const exludeColumns = [
      'รวม',
      'ยอดโอน',
      'COD',
      'ค่าคอม (ชิ้น)',
      'ค่าคอม %',
      'ค่าคอม (ORDER)',
    ]
    columns = columns.filter((column) => !exludeColumns.includes(column.title))
  }

  const expandedRowRender = (record: IUserDailyReport & { key: number }) => {
    const userCancelled = userCancelledReduce?.[record.value.username]
    const items = [record]
    if (userCancelled) {
      items.push({
        ...record,
        value: {
          ...userCancelled,
          cancelled: true,
        },
      })
    }
    let columns: any = [
      {
        title: '',
        dataIndex: 'empty',
        align: 'center' as const,
        key: 'comm-value',
        width: 130,
        render: (value: any, data: IUserDailyReport) => {
          return (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {data.value?.cancelled ? (
                <>
                  <Typography
                    variant="subtitle2"
                    textAlign={'center'}
                    sx={{
                      color: 'success.main',
                      cursor: 'pointer',
                      lineHeight: 1.2,
                    }}
                    minWidth={100}
                    onClick={() => onCancelledSelectUser(data?.value?.username)}
                  >
                    ยกเลิก/ตีกลับ
                  </Typography>
                </>
              ) : (
                <>
                  {searchFilter.viewType !== 'sale' ? (
                    <Typography
                      variant="subtitle2"
                      textAlign={'center'}
                      sx={{
                        color: 'success.main',
                        cursor: 'pointer',
                        lineHeight: 1.2,
                      }}
                      minWidth={100}
                      onClick={() => onSelectUser(data?.value?.username)}
                    >
                      ขายได้
                    </Typography>
                  ) : (
                    <Typography variant="subtitle2" textAlign={'center'}>
                      ขายได้
                    </Typography>
                  )}
                </>
              )}
            </Typography>
          )
        },
      },
      {
        title: 'รายการ',
        dataIndex: 'orderAmount',
        align: 'center' as const,
        key: 'comm-value',
        width: 30,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.count?.toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'จำนวนชิ้น',
        dataIndex: 'unitAmount',
        key: 'comm-unitAmount',
        align: 'center' as const,
        width: 75,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {data.value?.unit?.toLocaleString()}
            </Typography>
            {!hideText && (
              <>
                {!data.value?.cancelled ? (
                  <Typography
                    variant="subtitle2"
                    fontSize={11}
                    color="#aaa"
                    textAlign={'center'}
                  >
                    ({numberFormat(Number(data.value?.finalUnits ?? 0))})
                  </Typography>
                ) : (
                  <Typography
                    variant="subtitle2"
                    fontSize={11}
                    color="#aaa"
                    textAlign={'center'}
                  >
                    (
                    {numberFormat(
                      Number(data.value?.collectionUnits ?? 0) +
                        Number(data.value?.unitNonCollection ?? 0),
                    )}
                    )
                  </Typography>
                )}
              </>
            )}
          </>
        ),
      },
      {
        title: 'เซต',
        dataIndex: 'set',
        key: 'comm-set',
        align: 'center' as const,
        width: 60,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.collection?.toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'เซต(ชิ้น)',
        dataIndex: 'setPieces',
        key: 'comm-setPieces',
        align: 'center' as const,
        width: 60,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.collectionUnits?.toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'ฟรี(ชิ้น)',
        dataIndex: 'freePieces',
        key: 'comm-freePieces',
        align: 'center' as const,
        width: 60,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.free?.toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'COD',
        dataIndex: 'codPrice',
        key: 'comm-codPrice',
        align: 'center' as const,
        width: 70,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.codTotal?.toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'ยอดโอน',
        dataIndex: 'bankPrice',
        key: 'comm-bankPrice',
        align: 'center' as const,
        width: 70,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.bankTotal?.toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'รวม',
        dataIndex: 'total',
        align: 'center' as const,
        key: 'comm-total',
        width: 70,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {(
              (data.value?.bankTotal ?? 0) + data.value?.codTotal
            ).toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'ค่าคอม (ชิ้น)',
        dataIndex: 'commission',
        key: 'comm-commission',
        align: 'center' as const,
        width: 70,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.commission?.toLocaleString()}
          </Typography>
        ),
      },
      {
        title: 'ค่าคอม %',
        dataIndex: 'commissionPercent',
        key: 'comm-commissionPercent',
        align: 'center' as const,
        width: 70,
        render: (value: IUserDailyValueReport, data: IUserDailyReport) => (
          <>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              textAlign={'center'}
            >
              {data.value?.commissionPercent
                ? data.value?.commissionPercent?.toLocaleString()
                : '0'}
            </Typography>
            {!hideText && (
              <Typography
                variant="subtitle2"
                fontSize={11}
                color="#aaa"
                textAlign={'center'}
              >
                (
                {numberFormat(
                  (((data.value?.bankTotal ?? 0) + data.value?.codTotal) *
                    (merchant.config?.commissionPercent ?? 0)) /
                    100,
                )}
                )
              </Typography>
            )}
          </>
        ),
      },
      {
        title: 'ค่าคอม (ORDER)',
        dataIndex: 'commissionOrder',
        key: 'comm-commissionOrder',
        align: 'center' as const,
        width: 70,
        render: (
          commissionOrder: IUserDailyValueReport,
          data: IUserDailyReport,
        ) => (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            textAlign={'center'}
          >
            {data.value?.commissionOrder?.toLocaleString() ?? 0}
          </Typography>
        ),
      },
    ]

    if (!showAll) {
      const exludeColumns = [
        'รวม',
        'ยอดโอน',
        'COD',
        'ค่าคอม (ชิ้น)',
        'ค่าคอม %',
        'ค่าคอม (ORDER)',
      ]
      columns = columns.filter(
        (column: any) => !exludeColumns.includes(column.title),
      )
    }

    return (
      <Table
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre: any, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={items}
        pagination={false}
      />
    )
  }

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <div ref={ref}>
        <Table
          size={'middle'}
          columns={columns}
          scroll={{ x: columns.reduce((pre, cur) => cur.width + pre, 0) }}
          expandable={{
            expandedRowRender,
            rowExpandable: (record) => (hideText ? false : true),
          }}
          dataSource={report
            ?.sort((a, b) => a.value.username.localeCompare(b.value.username))
            .map((commission: IUserDailyReport, index: number) => {
              return {
                ...commission,
                key: index,
              }
            })}
          pagination={false}
          bordered
          summary={() => {
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    สรุปผล
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} key={'รายการ'}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {/* {(
                      sumBy(data, 'value.count') -
                      sumBy(userCancelledReport, 'count')
                    ).toLocaleString()} */}
                    {searchFilter.viewType !== 'sale'
                      ? numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.count)
                          }) -
                            sumBy(userCancelledReport, (o) => {
                              return Number(o.count)
                            }),
                        )
                      : numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.count)
                          }),
                        )}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} key={'จำนวนชิ้น'}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {searchFilter.viewType !== 'sale'
                      ? numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.unit)
                          }) -
                            sumBy(userCancelledReport, (o) => {
                              return Number(o.unit)
                            }),
                        )
                      : numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.unit)
                          }),
                        )}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} key={'เซต'}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {searchFilter.viewType !== 'sale'
                      ? numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.collection)
                          }) -
                            sumBy(userCancelledReport, (o) => {
                              return Number(o.collection)
                            }),
                        )
                      : numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.collection)
                          }),
                        )}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} key={'เซต(ชิ้น)'}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {searchFilter.viewType !== 'sale'
                      ? numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.collectionUnits)
                          }) -
                            sumBy(userCancelledReport, (o) => {
                              return Number(o.collectionUnits)
                            }),
                        )
                      : numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.collectionUnits)
                          }),
                        )}
                  </Typography>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={7} key={'ฟรี(ชิ้น)'}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {searchFilter.viewType !== 'sale'
                      ? numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.free)
                          }) -
                            sumBy(userCancelledReport, (o) => {
                              return Number(o.free)
                            }),
                        )
                      : numberFormat(
                          sumBy(data, (o) => {
                            return Number(o.value.free)
                          }),
                        )}
                  </Typography>
                </Table.Summary.Cell>
                {showAll && (
                  <>
                    <Table.Summary.Cell index={8} key={'COD'}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign={'center'}
                      >
                        {searchFilter.viewType !== 'sale'
                          ? numberFormat(
                              sumBy(data, (o) => {
                                return Number(o.value.codTotal)
                              }) -
                                sumBy(userCancelledReport, (o) => {
                                  return Number(o.codTotal)
                                }),
                            )
                          : numberFormat(
                              sumBy(data, (o) => {
                                return Number(o.value.codTotal)
                              }),
                            )}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} key={'ยอดโอน'}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign={'center'}
                      >
                        {searchFilter.viewType !== 'sale'
                          ? numberFormat(
                              sumBy(data, (o) => {
                                return Number(o.value.bankTotal)
                              }) -
                                sumBy(userCancelledReport, (o) => {
                                  return Number(o.bankTotal)
                                }),
                            )
                          : numberFormat(
                              sumBy(data, (o) => {
                                return Number(o.value.bankTotal)
                              }),
                            )}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} key={'รวม'}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign={'center'}
                      >
                        {searchFilter.viewType !== 'sale'
                          ? numberFormat(
                              sumBy(data, (o) => {
                                return (
                                  Number(o.value.bankTotal) +
                                  Number(o.value.codTotal)
                                )
                              }) -
                                (sumBy(userCancelledReport, 'bankTotal') +
                                  sumBy(userCancelledReport, 'codTotal')),
                            )
                          : numberFormat(
                              sumBy(data, (o) => {
                                return (
                                  Number(o.value.bankTotal) +
                                  Number(o.value.codTotal)
                                )
                              }),
                            )}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} key={'ค่าคอม (ชิ้น)'}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign={'center'}
                      >
                        {searchFilter.viewType !== 'sale'
                          ? numberFormat(
                              sumBy(data, (o) => {
                                return Number(o.value.commission)
                              }) -
                                sumBy(userCancelledReport, (o) => {
                                  return Number(o.commission)
                                }),
                            )
                          : numberFormat(
                              sumBy(data, (o) => {
                                return Number(o.value.commission)
                              }),
                            )}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} key={'ค่าคอม %'}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign={'center'}
                      >
                        {searchFilter.viewType !== 'sale'
                          ? numberFormat(
                              Number(
                                sumBy(data, 'value.commissionPercent') ?? 0,
                              ) -
                                sumBy(userCancelledReport, 'commissionPercent'),
                            )
                          : numberFormat(
                              Number(
                                sumBy(data, 'value.commissionPercent') ?? 0,
                              ),
                            )}
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        fontSize={11}
                        color="#aaa"
                        textAlign={'center'}
                      >
                        {searchFilter.viewType !== 'sale'
                          ? numberFormat(
                              ((sumBy(data, 'value.bankTotal') +
                                sumBy(data, 'value.codTotal') -
                                (sumBy(userCancelledReport, 'bankTotal') +
                                  sumBy(userCancelledReport, 'codTotal'))) *
                                (merchant?.config?.commissionPercent ?? 0)) /
                                100,
                            )
                          : numberFormat(
                              ((sumBy(data, 'value.bankTotal') +
                                sumBy(data, 'value.codTotal') -
                                (sumBy(userCancelledReport, 'bankTotal') +
                                  sumBy(userCancelledReport, 'codTotal'))) *
                                (merchant?.config?.commissionPercent ?? 0)) /
                                100,
                            )}
                      </Typography>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} key={'ค่าคอม (ORDER)'}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        textAlign={'center'}
                      >
                        {searchFilter.viewType !== 'sale'
                          ? numberFormat(
                              Number(
                                sumBy(data, 'value.commissionOrder') ?? 0,
                              ) - sumBy(userCancelledReport, 'commissionOrder'),
                            )
                          : numberFormat(
                              Number(sumBy(data, 'value.commissionOrder') ?? 0),
                            )}
                      </Typography>
                    </Table.Summary.Cell>
                  </>
                )}
              </Table.Summary.Row>
            )
          }}
        />
        <Typography
          variant="subtitle2"
          color="#ff0000"
          fontSize={12}
          sx={{ mt: 1 }}
        >
          * หมายเหตุ 1: ค่าคอมมิชชั่น % ภายในวงเล็บคำนวนจาก{' '}
          {merchant?.config?.commissionPercent}% ของยอดรวม ซึ่งคำนวนเรียลไทม์
        </Typography>
        <Typography
          variant="subtitle2"
          color="#ff0000"
          fontSize={12}
          sx={{ mt: 0.5 }}
        >
          * หมายเหตุ 2: จำนวนชิ้น ภายในวงเล็บ{' '}
          คือจำนวนชิ้นที่ขายได้โดยนับรวมจากสินค้าที่เป็นเซตและสินค้าซื้อเป็นชิ้นทั่วไป
        </Typography>
      </div>
    </>
  )
})

export default CommissionReportTable
